import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ShieldAlert, ShieldX, Wand2 } from "lucide-react";

const RISK = {
  "LOW": { c: "text-accent", bg: "bg-accent/15 border-accent/30", Icon: ShieldCheck },
  "REVIEW REQUIRED": { c: "text-primary", bg: "bg-primary/15 border-primary/30", Icon: ShieldAlert },
  "HIGH": { c: "text-destructive", bg: "bg-destructive/15 border-destructive/30", Icon: ShieldX },
};

export default function Compliance() {
  const [copy, setCopy] = useState("");
  const [claims, setClaims] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const check = async () => {
    if (!copy) { toast.error("Paste ad copy first"); return; }
    setLoading(true); setResult(null);
    try { const r = await api.post("/ai/compliance-check", { copy, claims }); setResult(r.data); }
    catch (e) { toast.error("Check failed"); }
    finally { setLoading(false); }
  };

  const rk = result && (RISK[result.overall_risk] || RISK["REVIEW REQUIRED"]);

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-float-up" data-testid="compliance-panel">
      <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
        <div className="space-y-2"><Label>Ad Copy</Label><Textarea data-testid="compliance-copy" rows={5} placeholder="Paste your ad copy here…" value={copy} onChange={(e) => setCopy(e.target.value)} /></div>
        <div className="space-y-2"><Label>Claims (optional)</Label><Input data-testid="compliance-claims" placeholder="e.g. guaranteed results, 100% cured" value={claims} onChange={(e) => setClaims(e.target.value)} /></div>
        <Button data-testid="compliance-check-btn" onClick={check} disabled={loading} className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ShieldCheck className="w-4 h-4 mr-1" /> Run AI Risk Check</>}
        </Button>
      </div>

      {result && (
        <div className="space-y-4 animate-float-up" data-testid="compliance-result">
          <div className={`rounded-2xl border p-5 flex items-center gap-4 ${rk.bg}`}>
            <rk.Icon className={`w-8 h-8 ${rk.c}`} />
            <div><p className="text-xs text-muted-foreground">Overall Risk</p><p className={`font-display text-xl font-bold ${rk.c}`} data-testid="compliance-risk-level">{result.overall_risk}</p></div>
          </div>

          {result.issues?.length > 0 && (
            <div className="bg-card border border-border rounded-2xl p-5 space-y-3">
              <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Issues Detected</p>
              {result.issues.map((it, i) => (
                <div key={i} className="border-l-2 border-primary/50 pl-3">
                  <p className="text-sm font-medium">{it.area} · <span className="text-primary">{it.risk}</span></p>
                  <p className="text-sm text-muted-foreground">{it.reason}</p>
                </div>
              ))}
            </div>
          )}

          {result.proposed_version && (
            <div className="bg-secondary/30 border border-accent/30 rounded-2xl p-5">
              <p className="text-xs font-mono uppercase tracking-widest text-accent mb-2 flex items-center gap-1"><Wand2 className="w-3 h-3" /> Fix With AI — Proposed Version</p>
              <p className="text-sm">{result.proposed_version}</p>
              {result.risk_change && <p className="text-xs text-accent mt-2">Risk change: {result.risk_change}</p>}
            </div>
          )}

          <p className="text-xs text-muted-foreground italic">{result.disclaimer || "Platform approval remains with the advertising provider."}</p>
        </div>
      )}
    </div>
  );
}
